// Code generated by backend/api/errorcodes/gen — DO NOT EDIT.
// Source: backend/api/errorcodes/codes.go

/**
 * Canonical error code registry. Every user-facing error code is defined in
 * the backend (backend/api/errorcodes/codes.go) and generated here so the
 * frontend shares the same vocabulary.
 *
 * To add a new code: edit codes.go, then run:
 *   go generate ./backend/api/errorcodes/...
 */

export type ErrorCodeDef<C extends string = string> = {
    readonly code: C;
    readonly userMessage: string;
};

export const CONTENT_GIT_ACCESS_DENIED: ErrorCodeDef<"CONTENT_GIT_ACCESS_DENIED"> = {
    code: "CONTENT_GIT_ACCESS_DENIED",
    userMessage: "Git access denied. Connect your account to access this repository.",
} as const;

export const CONTENT_GIT_AUTH_FAILED: ErrorCodeDef<"CONTENT_GIT_AUTH_FAILED"> = {
    code: "CONTENT_GIT_AUTH_FAILED",
    userMessage: "Git authentication failed. Your credentials may have expired.",
} as const;

export const CONTENT_GIT_HTTP_AUTH_DENIED: ErrorCodeDef<"CONTENT_GIT_HTTP_AUTH_DENIED"> = {
    code: "CONTENT_GIT_HTTP_AUTH_DENIED",
    userMessage: "Git HTTP authentication denied. Your password or token is incorrect or expired.",
} as const;

export const CONTENT_GIT_INVALID_TOKEN: ErrorCodeDef<"CONTENT_GIT_INVALID_TOKEN"> = {
    code: "CONTENT_GIT_INVALID_TOKEN",
    userMessage: "Git token is invalid or expired. Re-authenticate to refresh your credentials.",
} as const;

export const CONTENT_GIT_SSO_EXPIRED: ErrorCodeDef<"CONTENT_GIT_SSO_EXPIRED"> = {
    code: "CONTENT_GIT_SSO_EXPIRED",
    userMessage: "Your SSO session has expired. Re-authenticate to continue.",
} as const;

export const CONTENT_GIT_REPO_NOT_FOUND: ErrorCodeDef<"CONTENT_GIT_REPO_NOT_FOUND"> = {
    code: "CONTENT_GIT_REPO_NOT_FOUND",
    userMessage: "Repository not found. Check the URL and your access permissions.",
} as const;

export const CONTENT_INVALID_CLONE_TARGET: ErrorCodeDef<"CONTENT_INVALID_CLONE_TARGET"> = {
    code: "CONTENT_INVALID_CLONE_TARGET",
    userMessage: "Invalid clone target. Check your project configuration.",
} as const;

export const SCM_RATE_LIMITED: ErrorCodeDef<"SCM_RATE_LIMITED"> = {
    code: "SCM_RATE_LIMITED",
    userMessage: "SCM rate limit exceeded. Wait a moment and try again.",
} as const;

export const MACHINE_SSM_ACCESS_DENIED: ErrorCodeDef<"MACHINE_SSM_ACCESS_DENIED"> = {
    code: "MACHINE_SSM_ACCESS_DENIED",
    userMessage:
        "Your environment could not start due to a missing AWS permission. Ask your administrator to check the runner setup.",
} as const;

export const MACHINE_SUBNET_EXHAUSTED: ErrorCodeDef<"MACHINE_SUBNET_EXHAUSTED"> = {
    code: "MACHINE_SUBNET_EXHAUSTED",
    userMessage:
        "Your environment could not start because the network is full. Ask your administrator to free up resources or expand capacity.",
} as const;

export const MACHINE_VM_TERMINATED: ErrorCodeDef<"MACHINE_VM_TERMINATED"> = {
    code: "MACHINE_VM_TERMINATED",
    userMessage:
        "This environment was shut down externally and cannot be restarted. Please delete it and create a new one.",
} as const;

export const MACHINE_LOCAL_NETWORK: ErrorCodeDef<"MACHINE_LOCAL_NETWORK"> = {
    code: "MACHINE_LOCAL_NETWORK",
    userMessage:
        "Could not connect to the environment. Grant the Local Network permission in macOS System Preferences (Privacy & Security > Local Network).",
} as const;

export const MACHINE_AGENT_TIMEOUT: ErrorCodeDef<"MACHINE_AGENT_TIMEOUT"> = {
    code: "MACHINE_AGENT_TIMEOUT",
    userMessage: "The environment became unresponsive and was shut down. Try creating a new environment.",
} as const;

export const MACHINE_SNAPSHOT_FAILED: ErrorCodeDef<"MACHINE_SNAPSHOT_FAILED"> = {
    code: "MACHINE_SNAPSHOT_FAILED",
    userMessage:
        "The prebuild could not be saved in time. Retry the prebuild, or contact support if this keeps happening.",
} as const;

export const MACHINE_CONFIG_DELIVERY_FAILED: ErrorCodeDef<"MACHINE_CONFIG_DELIVERY_FAILED"> = {
    code: "MACHINE_CONFIG_DELIVERY_FAILED",
    userMessage:
        "Your environment could not be configured. This is usually temporary. If it persists, ask your administrator to check the runner logs.",
} as const;

export const MACHINE_OUT_OF_CAPACITY: ErrorCodeDef<"MACHINE_OUT_OF_CAPACITY"> = {
    code: "MACHINE_OUT_OF_CAPACITY",
    userMessage:
        "No capacity available to start your environment. Try again in a few minutes, or ask your administrator to add resources.",
} as const;

export const MACHINE_UNEXPECTED_EXIT: ErrorCodeDef<"MACHINE_UNEXPECTED_EXIT"> = {
    code: "MACHINE_UNEXPECTED_EXIT",
    userMessage: "The environment stopped unexpectedly. Try restarting or creating a new one.",
} as const;

export const MACHINE_SYSTEM_EXCEPTION: ErrorCodeDef<"MACHINE_SYSTEM_EXCEPTION"> = {
    code: "MACHINE_SYSTEM_EXCEPTION",
    userMessage:
        "Something went wrong while starting your environment. Try creating a new one, or contact support if this keeps happening.",
} as const;

export const AUTOMATIONS_FILE_NOT_FOUND: ErrorCodeDef<"AUTOMATIONS_FILE_NOT_FOUND"> = {
    code: "AUTOMATIONS_FILE_NOT_FOUND",
    userMessage: "Automations file not found. Check that the file exists in your repository.",
} as const;

export const AUTOMATIONS_FILE_PARSE_ERROR: ErrorCodeDef<"AUTOMATIONS_FILE_PARSE_ERROR"> = {
    code: "AUTOMATIONS_FILE_PARSE_ERROR",
    userMessage: "Automations file has a syntax error. Check the file format.",
} as const;

export const AUTOMATIONS_FILE_PERMISSION_DENIED: ErrorCodeDef<"AUTOMATIONS_FILE_PERMISSION_DENIED"> = {
    code: "AUTOMATIONS_FILE_PERMISSION_DENIED",
    userMessage: "Permission denied reading automations file.",
} as const;

export const DEVCONTAINER_RECOVERY_FAILED: ErrorCodeDef<"DEVCONTAINER_RECOVERY_FAILED"> = {
    code: "DEVCONTAINER_RECOVERY_FAILED",
    userMessage:
        "Recovery container creation failed. The environment may not be accessible. Try creating a new environment.",
} as const;

export const DEVCONTAINER_DNS_FAILED: ErrorCodeDef<"DEVCONTAINER_DNS_FAILED"> = {
    code: "DEVCONTAINER_DNS_FAILED",
    userMessage:
        "DNS resolution failed. If you're on a restricted network, check that your runner can reach the required registries.",
} as const;

export const DEVCONTAINER_REGISTRY_ERROR: ErrorCodeDef<"DEVCONTAINER_REGISTRY_ERROR"> = {
    code: "DEVCONTAINER_REGISTRY_ERROR",
    userMessage: "Container image pull failed. Check that the image exists and your runner has access to the registry.",
} as const;

export const DEVCONTAINER_DOCKERFILE_ERROR: ErrorCodeDef<"DEVCONTAINER_DOCKERFILE_ERROR"> = {
    code: "DEVCONTAINER_DOCKERFILE_ERROR",
    userMessage: "Dockerfile syntax error. Check your Dockerfile for errors.",
} as const;

export const DEVCONTAINER_BUILD_FAILED: ErrorCodeDef<"DEVCONTAINER_BUILD_FAILED"> = {
    code: "DEVCONTAINER_BUILD_FAILED",
    userMessage:
        "Dev container build failed. Check the build logs for details. You can rebuild after fixing the issue.",
} as const;

export const DEVCONTAINER_CREATE_FAILED: ErrorCodeDef<"DEVCONTAINER_CREATE_FAILED"> = {
    code: "DEVCONTAINER_CREATE_FAILED",
    userMessage: "Dev container creation failed. Check the logs for details.",
} as const;

export const SESSION_TIMEOUT: ErrorCodeDef<"SESSION_TIMEOUT"> = {
    code: "SESSION_TIMEOUT",
    userMessage: "The request timed out. This could be temporary, please try again.",
} as const;

export const SESSION_UNAVAILABLE: ErrorCodeDef<"SESSION_UNAVAILABLE"> = {
    code: "SESSION_UNAVAILABLE",
    userMessage: "The service is temporarily unavailable. Please try again in a moment.",
} as const;

export const SESSION_IMAGE_RATE_LIMITED: ErrorCodeDef<"SESSION_IMAGE_RATE_LIMITED"> = {
    code: "SESSION_IMAGE_RATE_LIMITED",
    userMessage: "You've sent too many images recently. Please wait a moment and try again.",
} as const;

export const SESSION_RATE_LIMITED: ErrorCodeDef<"SESSION_RATE_LIMITED"> = {
    code: "SESSION_RATE_LIMITED",
    userMessage: "Too many requests. Please wait a moment and try again.",
} as const;

export const SESSION_UNAUTHENTICATED: ErrorCodeDef<"SESSION_UNAUTHENTICATED"> = {
    code: "SESSION_UNAUTHENTICATED",
    userMessage: "Your session has expired. Please sign in again.",
} as const;

export const SESSION_PERMISSION_DENIED: ErrorCodeDef<"SESSION_PERMISSION_DENIED"> = {
    code: "SESSION_PERMISSION_DENIED",
    userMessage: "You don't have permission to perform this action.",
} as const;

export const SESSION_INTERNAL: ErrorCodeDef<"SESSION_INTERNAL"> = {
    code: "SESSION_INTERNAL",
    userMessage: "Something went wrong. Please try again or contact support if the issue persists.",
} as const;

export const SESSION_RUNNER_NO_AGENT_SUPPORT: ErrorCodeDef<"SESSION_RUNNER_NO_AGENT_SUPPORT"> = {
    code: "SESSION_RUNNER_NO_AGENT_SUPPORT",
    userMessage: "This runner does not support Ona sessions. Ask your administrator to update the runner.",
} as const;

export const SESSION_IMAGES_DISABLED: ErrorCodeDef<"SESSION_IMAGES_DISABLED"> = {
    code: "SESSION_IMAGES_DISABLED",
    userMessage: "Image attachments are not enabled for your organization.",
} as const;

export const SESSION_STREAM_CLOSED: ErrorCodeDef<"SESSION_STREAM_CLOSED"> = {
    code: "SESSION_STREAM_CLOSED",
    userMessage: "The conversation stream was interrupted. Reconnecting…",
} as const;

export const SESSION_STREAM_FAILED: ErrorCodeDef<"SESSION_STREAM_FAILED"> = {
    code: "SESSION_STREAM_FAILED",
    userMessage: "Unable to connect to the conversation. This is usually temporary. Reconnecting…",
} as const;

export const SESSION_NETWORK_ERROR: ErrorCodeDef<"SESSION_NETWORK_ERROR"> = {
    code: "SESSION_NETWORK_ERROR",
    userMessage: "Network connection lost. Check your internet connection.",
} as const;

export const ENVIRONMENT_SECRETS_FAILED: ErrorCodeDef<"ENVIRONMENT_SECRETS_FAILED"> = {
    code: "ENVIRONMENT_SECRETS_FAILED",
    userMessage: "Failed to load secrets for this environment. Check your secret configuration and try again.",
} as const;

export const ENVIRONMENT_SECURITY_AGENT_FAILED: ErrorCodeDef<"ENVIRONMENT_SECURITY_AGENT_FAILED"> = {
    code: "ENVIRONMENT_SECURITY_AGENT_FAILED",
    userMessage:
        "Failed to initialize the security agent. Ask your administrator to verify the security agent configuration.",
} as const;

export const AGENT_NOT_FOUND: ErrorCodeDef<"AGENT_NOT_FOUND"> = {
    code: "AGENT_NOT_FOUND",
    userMessage: "The requested agent could not be found.",
} as const;

export const AGENT_LOAD_FAILED: ErrorCodeDef<"AGENT_LOAD_FAILED"> = {
    code: "AGENT_LOAD_FAILED",
    userMessage: "Failed to load the agent. Please try again.",
} as const;

export const AGENT_SUBAGENT_LIMIT_REACHED: ErrorCodeDef<"AGENT_SUBAGENT_LIMIT_REACHED"> = {
    code: "AGENT_SUBAGENT_LIMIT_REACHED",
    userMessage: "The maximum number of concurrent sub-agents has been reached.",
} as const;

export const AGENT_SUBAGENT_LOOKUP_FAILED: ErrorCodeDef<"AGENT_SUBAGENT_LOOKUP_FAILED"> = {
    code: "AGENT_SUBAGENT_LOOKUP_FAILED",
    userMessage: "Could not verify sub-agent concurrency. Please try again.",
} as const;

export const BILLING_RECONCILE_FAILED: ErrorCodeDef<"BILLING_RECONCILE_FAILED"> = {
    code: "BILLING_RECONCILE_FAILED",
    userMessage: "Failed to update your billing configuration. Please try again or contact support.",
} as const;

export const BILLING_SETUP_TRANSIENT_FAILURE: ErrorCodeDef<"BILLING_SETUP_TRANSIENT_FAILURE"> = {
    code: "BILLING_SETUP_TRANSIENT_FAILURE",
    userMessage: "We couldn't complete enterprise billing setup. Please retry in a moment.",
} as const;

export const BILLING_SETUP_STATE_INCONSISTENT: ErrorCodeDef<"BILLING_SETUP_STATE_INCONSISTENT"> = {
    code: "BILLING_SETUP_STATE_INCONSISTENT",
    userMessage: "Enterprise billing setup partially completed. Please retry to finish.",
} as const;

export const BILLING_USAGE_QUERY_FAILED: ErrorCodeDef<"BILLING_USAGE_QUERY_FAILED"> = {
    code: "BILLING_USAGE_QUERY_FAILED",
    userMessage: "Failed to retrieve credit usage data. Please try again.",
} as const;

export const INTERNAL_UNCLASSIFIED: ErrorCodeDef<"INTERNAL_UNCLASSIFIED"> = {
    code: "INTERNAL_UNCLASSIFIED",
    userMessage: "Something went wrong. Please try again or contact support if the issue persists.",
} as const;

/** All registered error codes, keyed by code string. */
export const ERROR_CODE_REGISTRY: Readonly<Record<string, ErrorCodeDef>> = {
    [CONTENT_GIT_ACCESS_DENIED.code]: CONTENT_GIT_ACCESS_DENIED,
    [CONTENT_GIT_AUTH_FAILED.code]: CONTENT_GIT_AUTH_FAILED,
    [CONTENT_GIT_HTTP_AUTH_DENIED.code]: CONTENT_GIT_HTTP_AUTH_DENIED,
    [CONTENT_GIT_INVALID_TOKEN.code]: CONTENT_GIT_INVALID_TOKEN,
    [CONTENT_GIT_SSO_EXPIRED.code]: CONTENT_GIT_SSO_EXPIRED,
    [CONTENT_GIT_REPO_NOT_FOUND.code]: CONTENT_GIT_REPO_NOT_FOUND,
    [CONTENT_INVALID_CLONE_TARGET.code]: CONTENT_INVALID_CLONE_TARGET,
    [SCM_RATE_LIMITED.code]: SCM_RATE_LIMITED,
    [MACHINE_SSM_ACCESS_DENIED.code]: MACHINE_SSM_ACCESS_DENIED,
    [MACHINE_SUBNET_EXHAUSTED.code]: MACHINE_SUBNET_EXHAUSTED,
    [MACHINE_VM_TERMINATED.code]: MACHINE_VM_TERMINATED,
    [MACHINE_LOCAL_NETWORK.code]: MACHINE_LOCAL_NETWORK,
    [MACHINE_AGENT_TIMEOUT.code]: MACHINE_AGENT_TIMEOUT,
    [MACHINE_SNAPSHOT_FAILED.code]: MACHINE_SNAPSHOT_FAILED,
    [MACHINE_CONFIG_DELIVERY_FAILED.code]: MACHINE_CONFIG_DELIVERY_FAILED,
    [MACHINE_OUT_OF_CAPACITY.code]: MACHINE_OUT_OF_CAPACITY,
    [MACHINE_UNEXPECTED_EXIT.code]: MACHINE_UNEXPECTED_EXIT,
    [MACHINE_SYSTEM_EXCEPTION.code]: MACHINE_SYSTEM_EXCEPTION,
    [AUTOMATIONS_FILE_NOT_FOUND.code]: AUTOMATIONS_FILE_NOT_FOUND,
    [AUTOMATIONS_FILE_PARSE_ERROR.code]: AUTOMATIONS_FILE_PARSE_ERROR,
    [AUTOMATIONS_FILE_PERMISSION_DENIED.code]: AUTOMATIONS_FILE_PERMISSION_DENIED,
    [DEVCONTAINER_RECOVERY_FAILED.code]: DEVCONTAINER_RECOVERY_FAILED,
    [DEVCONTAINER_DNS_FAILED.code]: DEVCONTAINER_DNS_FAILED,
    [DEVCONTAINER_REGISTRY_ERROR.code]: DEVCONTAINER_REGISTRY_ERROR,
    [DEVCONTAINER_DOCKERFILE_ERROR.code]: DEVCONTAINER_DOCKERFILE_ERROR,
    [DEVCONTAINER_BUILD_FAILED.code]: DEVCONTAINER_BUILD_FAILED,
    [DEVCONTAINER_CREATE_FAILED.code]: DEVCONTAINER_CREATE_FAILED,
    [SESSION_TIMEOUT.code]: SESSION_TIMEOUT,
    [SESSION_UNAVAILABLE.code]: SESSION_UNAVAILABLE,
    [SESSION_IMAGE_RATE_LIMITED.code]: SESSION_IMAGE_RATE_LIMITED,
    [SESSION_RATE_LIMITED.code]: SESSION_RATE_LIMITED,
    [SESSION_UNAUTHENTICATED.code]: SESSION_UNAUTHENTICATED,
    [SESSION_PERMISSION_DENIED.code]: SESSION_PERMISSION_DENIED,
    [SESSION_INTERNAL.code]: SESSION_INTERNAL,
    [SESSION_RUNNER_NO_AGENT_SUPPORT.code]: SESSION_RUNNER_NO_AGENT_SUPPORT,
    [SESSION_IMAGES_DISABLED.code]: SESSION_IMAGES_DISABLED,
    [SESSION_STREAM_CLOSED.code]: SESSION_STREAM_CLOSED,
    [SESSION_STREAM_FAILED.code]: SESSION_STREAM_FAILED,
    [SESSION_NETWORK_ERROR.code]: SESSION_NETWORK_ERROR,
    [ENVIRONMENT_SECRETS_FAILED.code]: ENVIRONMENT_SECRETS_FAILED,
    [ENVIRONMENT_SECURITY_AGENT_FAILED.code]: ENVIRONMENT_SECURITY_AGENT_FAILED,
    [AGENT_NOT_FOUND.code]: AGENT_NOT_FOUND,
    [AGENT_LOAD_FAILED.code]: AGENT_LOAD_FAILED,
    [AGENT_SUBAGENT_LIMIT_REACHED.code]: AGENT_SUBAGENT_LIMIT_REACHED,
    [AGENT_SUBAGENT_LOOKUP_FAILED.code]: AGENT_SUBAGENT_LOOKUP_FAILED,
    [BILLING_RECONCILE_FAILED.code]: BILLING_RECONCILE_FAILED,
    [BILLING_SETUP_TRANSIENT_FAILURE.code]: BILLING_SETUP_TRANSIENT_FAILURE,
    [BILLING_SETUP_STATE_INCONSISTENT.code]: BILLING_SETUP_STATE_INCONSISTENT,
    [BILLING_USAGE_QUERY_FAILED.code]: BILLING_USAGE_QUERY_FAILED,
    [INTERNAL_UNCLASSIFIED.code]: INTERNAL_UNCLASSIFIED,
} as const;

/** Union of all registered error code strings. */
export type ErrorCode =
    | "CONTENT_GIT_ACCESS_DENIED"
    | "CONTENT_GIT_AUTH_FAILED"
    | "CONTENT_GIT_HTTP_AUTH_DENIED"
    | "CONTENT_GIT_INVALID_TOKEN"
    | "CONTENT_GIT_SSO_EXPIRED"
    | "CONTENT_GIT_REPO_NOT_FOUND"
    | "CONTENT_INVALID_CLONE_TARGET"
    | "SCM_RATE_LIMITED"
    | "MACHINE_SSM_ACCESS_DENIED"
    | "MACHINE_SUBNET_EXHAUSTED"
    | "MACHINE_VM_TERMINATED"
    | "MACHINE_LOCAL_NETWORK"
    | "MACHINE_AGENT_TIMEOUT"
    | "MACHINE_SNAPSHOT_FAILED"
    | "MACHINE_CONFIG_DELIVERY_FAILED"
    | "MACHINE_OUT_OF_CAPACITY"
    | "MACHINE_UNEXPECTED_EXIT"
    | "MACHINE_SYSTEM_EXCEPTION"
    | "AUTOMATIONS_FILE_NOT_FOUND"
    | "AUTOMATIONS_FILE_PARSE_ERROR"
    | "AUTOMATIONS_FILE_PERMISSION_DENIED"
    | "DEVCONTAINER_RECOVERY_FAILED"
    | "DEVCONTAINER_DNS_FAILED"
    | "DEVCONTAINER_REGISTRY_ERROR"
    | "DEVCONTAINER_DOCKERFILE_ERROR"
    | "DEVCONTAINER_BUILD_FAILED"
    | "DEVCONTAINER_CREATE_FAILED"
    | "SESSION_TIMEOUT"
    | "SESSION_UNAVAILABLE"
    | "SESSION_IMAGE_RATE_LIMITED"
    | "SESSION_RATE_LIMITED"
    | "SESSION_UNAUTHENTICATED"
    | "SESSION_PERMISSION_DENIED"
    | "SESSION_INTERNAL"
    | "SESSION_RUNNER_NO_AGENT_SUPPORT"
    | "SESSION_IMAGES_DISABLED"
    | "SESSION_STREAM_CLOSED"
    | "SESSION_STREAM_FAILED"
    | "SESSION_NETWORK_ERROR"
    | "ENVIRONMENT_SECRETS_FAILED"
    | "ENVIRONMENT_SECURITY_AGENT_FAILED"
    | "AGENT_NOT_FOUND"
    | "AGENT_LOAD_FAILED"
    | "AGENT_SUBAGENT_LIMIT_REACHED"
    | "AGENT_SUBAGENT_LOOKUP_FAILED"
    | "BILLING_RECONCILE_FAILED"
    | "BILLING_SETUP_TRANSIENT_FAILURE"
    | "BILLING_SETUP_STATE_INCONSISTENT"
    | "BILLING_USAGE_QUERY_FAILED"
    | "INTERNAL_UNCLASSIFIED";
